import { useState, useCallback } from 'react';
import * as api from '../services/api';
import { useEntities } from './useEntities';

export function useSemanticSearch() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastQuery, setLastQuery] = useState('');
  const { entities } = useEntities();

  const calculateRelevanceScore = (entity, queryTerms) => {
    const text = `${entity.name} ${entity.description || ''}`.toLowerCase();
    let score = 0;
    let matches = 0;

    queryTerms.forEach(term => {
      const termRegex = new RegExp(term, 'gi');
      const nameMatches = (entity.name.match(termRegex) || []).length;
      const descMatches = ((entity.description || '').match(termRegex) || []).length;

      if (nameMatches > 0) {
        score += 0.5 * nameMatches;
        matches++;
      }
      if (descMatches > 0) {
        score += 0.3 * descMatches;
        matches++;
      }
    });

    // Normalize score between 0 and 1
    return Math.min(score / (queryTerms.length * 2), 1.0);
  };

  const search = useCallback(async (query, options = {}) => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    setError(null);
    setLastQuery(query);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 300));

      const queryLower = query.toLowerCase();
      const queryTerms = queryLower.split(/\s+/).filter(t => t.length > 2);
      const entityTypes = options.entityTypes || [];

      // Search through all entities
      const searchResults = entities
        .map(entity => ({
          ...entity,
          entity_id: entity.id,
          score: calculateRelevanceScore(entity, queryTerms)
        }))
        .filter(r => {
          // Filter by relevance
          if (r.score === 0) return false;

          // Filter by entity type if specified
          if (entityTypes.length > 0 && !entityTypes.includes(r.entity_type)) {
            return false;
          }

          return true;
        })
        .sort((a, b) => b.score - a.score)
        .slice(0, 50); // Limit to top 50 results

      setResults(searchResults);
    } catch (err) {
      setError(err.message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [entities]);

  const clear = useCallback(() => {
    setResults([]);
    setLastQuery('');
    setError(null);
  }, []);

  return {
    results,
    loading,
    error,
    lastQuery,
    search,
    clear,
  };
}

export function useGraphQuery() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const query = useCallback(async (params) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.graphQuery(params);
      setResult(response);
      return response;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    result,
    loading,
    error,
    query,
  };
}
